/**
 * Factory for creating map layers
 */
import TileLayer from 'ol/layer/Tile.js';
import XYZ from 'ol/source/XYZ.js';
import TileWMS from 'ol/source/TileWMS.js';
import Overlay from 'ol/Overlay.js';
import { CONFIG } from '../config/config.js';
import { AppState } from '../controllers/appState.js';

export const LayerFactory = {
  /**
   * Creates basemap layers for the map
   */
  createBasemapLayers() {
    const basemapLayers = {};
    
    Object.entries(CONFIG.basemaps).forEach(([key, config]) => {
      basemapLayers[key] = new TileLayer({
        source: new XYZ({
          url: config.url,
          attributions: config.attributions,
          tilePixelRatio: 1
        }),
        visible: key === AppState.basemap // Use AppState.basemap instead of hardcoded 'osm'
      });
    });
    
    return basemapLayers;
  },
  
  /**
   * Creates a watershed layer for a specific sigma value, cell size, and type
   */
  createWatershedLayer(sigma, type, cellSize = CONFIG.defaultCellSize, visible = false) {
    const layerName = type === 'prpt' 
      ? `watersheds:pour_points_cont_us_cell_${cellSize}_sigma_${sigma}`
      : `watersheds_poly_cont_us_cell_${cellSize}_sigma_${sigma}`;
      
    return new TileLayer({
      id: `watersheds-${type}-${cellSize}-${sigma}`,
      source: new TileWMS({
        url: CONFIG.geoserverUrl,
        params: {
          'LAYERS': layerName,
          'VERSION': '1.3.0',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'TILED': true,
          'BUFFER': type === 'prpt' ? 30 : 0  // Only add buffer for points layers
        },
        serverType: 'geoserver',
        projection: 'EPSG:3857'
      }),
      visible: visible
    });
  },
  
  /**
   * Creates all watershed layers for all sigma values and cell sizes
   */
  createWatershedLayers(cellSize = CONFIG.defaultCellSize) {
    const boundaryLayers = {};
    const pointsLayers = {};
    
    CONFIG.sigmaValues.forEach(sigma => {
      boundaryLayers[sigma] = this.createWatershedLayer(sigma, 'bndry', cellSize);
      pointsLayers[sigma] = this.createWatershedLayer(sigma, 'prpt', cellSize, sigma === CONFIG.defaultSigma);
    });
    
    return { boundaryLayers, pointsLayers };
  },

  /**
   * Creates a reference layer based on config key
   * @param {string} layerKey - Key in CONFIG.referenceLayers
   */
  createReferenceLayer(layerKey) {
    const layerConfig = CONFIG.referenceLayers[layerKey];
    
    // Return null for 'none' since it's not an actual layer
    if (layerKey === 'none') return null;
    
    // Make sure the layer exists in config
    if (!layerConfig || !layerConfig.geoserverLayer) {
      console.error(`Reference layer config not found for key: ${layerKey}`);
      return null;
    }
    
    return new TileLayer({
      source: new TileWMS({
        url: CONFIG.geoserverUrl,
        params: {
          'LAYERS': layerConfig.geoserverLayer,
          'VERSION': '1.3.0',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'TILED': true
        },
        serverType: 'geoserver',
        projection: 'EPSG:3857'
      }),
      visible: false
    });
  },
  
  /**
   * Creates all reference layers
   * @returns {Object} Object with reference layers
   */
  createReferenceLayers() {
    const referenceLayers = {};
    
    Object.keys(CONFIG.referenceLayers).forEach(layerKey => {
      if (layerKey !== 'none') { // Skip 'none' as it's not a real layer
        referenceLayers[layerKey] = this.createReferenceLayer(layerKey);
      }
    });
    
    return referenceLayers;
  },
  
  /**
   * Creates a popup overlay for the map
   */
  createPopupOverlay() {
    // Use the existing popup element from the HTML
    const popupElement = document.getElementById('popup');
    
    if (!popupElement) {
      console.error('Popup element not found in the DOM');
      return null;
    }
    
    // Create popup overlay
    return new Overlay({
      element: popupElement,
      autoPan: true,
      autoPanAnimation: {
        duration: 250
      }
    });
  }
};

export default LayerFactory;